import { useState, useEffect, useMemo } from "react";
import {
  Alert,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Typography,
  Box,
  Divider,
  IconButton,
} from "@mui/material";
import DsButton from "@accredo/fe-shared/theme/components/Button.jsx";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import FileGrid from "./FileGrid.jsx";
import DropZone from "./DropZone.jsx";
import PropTypes from "prop-types";

/**
 * Modal for selecting existing/session files and uploading new ones.
 *
 * Internal selection state is kept local and only committed to the parent
 * on Confirm — Cancel leaves the field value untouched.
 *
 * Props:
 *   open                         — boolean
 *   onClose                      — () => void
 *   onConfirm                    — (selectedIds: string[]) => void
 *   existingFiles                — file[]
 *   sessionFiles                 — file[] (uploaded this session)
 *   onAddSessionFile             — (file: File) => string  returns new client UUID
 *   onUpdateSessionFileDescription — (id: string, description: string) => void
 *   initialSelectedIds           — string[]  the field's current value on open
 *   multiple                     — boolean
 *   accept                       — string  forwarded to DropZone
 *   title                        — string  override dialog title
 *   addDescription               — boolean  allow editing file descriptions
 *   addUsedIn                    — boolean  allow tagging source categorization on session files
 *   usedInSuggestions            — string[]  autocomplete suggestions from claim's existing tags
 *   onUpdateSessionFileUsedIn    — (uuid: string, tags: string[]) => void
 *   unselectableIds              — (string|number)[]  IDs that cannot be selected (e.g., already used in costs)
 *   showUnselectableAsSelected   — boolean  show unselectable files as selected (default: false)
 */
export default function FilePickerDialog({
  open,
  onClose,
  onConfirm,
  existingFiles = [],
  sessionFiles = [],
  onAddSessionFile,
  onUpdateSessionFileDescription,
  onUpdateSessionFileUsedIn,
  initialSelectedIds = [],
  multiple = false,
  accept = [],
  title,
  addDescription = false,
  addUsedIn = false,
  usedInSuggestions = [],
  unselectableIds = [],
  showUnselectableAsSelected = false,
  showUsedInFilter = false,
}) {
  const [selectedIds, setSelectedIds] = useState(initialSelectedIds);
  const [search, setSearch] = useState("");
  const [activeTagFilter, setActiveTagFilter] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // sync local selection whenever the dialog opens
  useEffect(() => {
    if (open) {
      setSelectedIds(initialSelectedIds);
      setSearch("");
      setActiveTagFilter(null);
      setUploadError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isUnselectable = (id) =>
    unselectableIds.map(Number).includes(Number(id));

  const toggleFile = (id) => {
    if (isUnselectable(id)) {
      return;
    }

    if (multiple) {
      setSelectedIds((prev) => {
        const newIds = prev.includes(id)
          ? prev.filter((s) => s !== id)
          : [...prev, id];
        return newIds;
      });
    } else {
      // single mode: toggle
      setSelectedIds((prev) => {
        const newIds = prev[0] === id ? [] : [id];
        return newIds;
      });
    }
  };

  const handleDrop = async (files) => {
    setIsUploading(true);
    setUploadError(null);

    try {
      for (const file of files) {
        const result = await onAddSessionFile(file);
        const fileId = result?.id;
        setSelectedIds((prev) => {
          const newIds = multiple ? [...prev, fileId] : [fileId];
          return newIds;
        });
      }
    } catch (err) {
      console.error("upload error:", err);
      const message =
        err?.response?.data?.message ?? err?.message ?? "Upload fehlgeschlagen";
      setUploadError(message);
    } finally {
      setIsUploading(false);
    }
  };

  const allTags = useMemo(() => {
    const tags = [...existingFiles, ...sessionFiles].flatMap(
      (f) => f.used_in ?? []
    );
    return [...new Set(tags)].sort();
  }, [existingFiles, sessionFiles]);

  const filter = (files) => {
    const q = search.trim().toLowerCase();
    let result = q
      ? files.filter((f) => f.name.toLowerCase().includes(q))
      : files;
    if (activeTagFilter) {
      result = result.filter((f) =>
        (f.used_in ?? []).includes(activeTagFilter)
      );
    }
    return result;
  };

  const filteredExisting = filter(existingFiles);
  const filteredSession = filter(sessionFiles);

  const dialogTitle =
    title ??
    (multiple
      ? "Dateien auswählen oder hochladen"
      : "Datei auswählen oder hochladen");

  const confirmLabel =
    selectedIds.length > 0
      ? `Übernehmen${multiple || selectedIds.length > 1 ? ` (${selectedIds.length})` : ""}`
      : "Übernehmen";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="file-picker-title"
    >
      <DialogTitle id="file-picker-title" sx={{ pr: 6 }}>
        {dialogTitle}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          minHeight: 320,
        }}
      >
        {/* Search */}
        <TextField
          placeholder="Anlagen suchen…"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoComplete="off"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />

        {/* Tag filter chips */}
        {showUsedInFilter && allTags.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {allTags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                variant={activeTagFilter === tag ? "filled" : "outlined"}
                color={activeTagFilter === tag ? "primary" : "default"}
                onClick={() =>
                  setActiveTagFilter((prev) => (prev === tag ? null : tag))
                }
                sx={{ fontSize: "0.7rem" }}
              />
            ))}
          </Box>
        )}

        {/* Existing files */}
        {existingFiles.length > 0 && (
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              component="h3"
              sx={{ lineHeight: 1.6 }}
            >
              Bestehende Anlagen
            </Typography>
            <FileGrid
              files={filteredExisting}
              selectedIds={selectedIds}
              onToggle={toggleFile}
              multiple={multiple}
              emptyText="Keine Anlagen zu Ihrem Suchbegriff gefunden."
              addDescription={false}
              showUsedInTags={showUsedInFilter}
              unselectableIds={unselectableIds}
              showUnselectableAsSelected={showUnselectableAsSelected}
            />
          </Box>
        )}

        {/* Session files — only shown once at least one has been added this session */}
        {sessionFiles.length > 0 && (
          <>
            {existingFiles.length > 0 && <Divider />}
            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                component="h3"
                sx={{ lineHeight: 1.6 }}
              >
                Gerade hochgeladen
              </Typography>
              <FileGrid
                files={filteredSession}
                selectedIds={selectedIds}
                onToggle={toggleFile}
                multiple={multiple}
                emptyText="Keine neu hochlgeladenen Anlagen zu Ihrem Suchbegriff gefunden."
                addDescription={addDescription}
                onUpdateDescription={onUpdateSessionFileDescription}
                addUsedIn={addUsedIn}
                usedInSuggestions={usedInSuggestions}
                onUpdateUsedIn={onUpdateSessionFileUsedIn}
                showUsedInTags={showUsedInFilter}
                unselectableIds={unselectableIds}
                showUnselectableAsSelected={showUnselectableAsSelected}
              />
            </Box>
          </>
        )}

        {/* initial state for new claims without any session or existing files */}
        {existingFiles.length === 0 && sessionFiles.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Noch keine Anlagen. Laden Sie eine Datei hoch.
          </Typography>
        )}

        <Divider sx={{ mt: "auto" }} />

        <DropZone
          onDrop={handleDrop}
          accept={accept}
          multiple={multiple}
          disabled={isUploading}
        />

        {isUploading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Datei wird hochgeladen…
            </Typography>
          </Box>
        )}

        {uploadError && (
          <Alert
            severity="error"
            sx={{ mt: 1 }}
            onClose={() => setUploadError(null)}
          >
            {uploadError}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <DsButton onClick={onClose} variant="tertiary" disabled={isUploading}>
          Abbrechen
        </DsButton>
        <DsButton
          variant="primary"
          onClick={() => {
            onConfirm(selectedIds);
          }}
          disabled={selectedIds.length === 0 || isUploading}
        >
          {confirmLabel}
        </DsButton>
      </DialogActions>
    </Dialog>
  );
}

FilePickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  existingFiles: PropTypes.array,
  sessionFiles: PropTypes.array,
  onAddSessionFile: PropTypes.func.isRequired,
  onUpdateSessionFileDescription: PropTypes.func,
  initialSelectedIds: PropTypes.array,
  multiple: PropTypes.bool,
  accept: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string,
  addDescription: PropTypes.bool,
  unselectableIds: PropTypes.array,
  showUnselectableAsSelected: PropTypes.bool,
};
